/**
 * Orbit-closure analysis via kinetic-energy minima.
 *
 * For a (near-)periodic orbit the total kinetic energy has a deep minimum once
 * per loop — the turning point, where the system momentarily slows before
 * reversing. Those turning points are a natural Poincaré-like section: on a
 * truly periodic orbit every turning point coincides in phase space, so the time
 * between successive minima IS the period, and the drift of each minimum's
 * phase-space position away from the first one measures how far the orbit has
 * strayed from closing. This is far more robust for near-periodic motion than
 * spectral/autocorrelation methods, which is why it gets its own panel.
 */

export interface ClosurePoint {
  /** Time of this turning point (s). */
  time: number;
  /** Duration of the loop ENDING at this point (s), i.e. time since the previous minimum. Null for the first. */
  period: number | null;
  /** Normalised phase-space distance from the first turning point (0 = perfect closure). */
  stray: number;
}

export interface ClosureResult {
  /** First inter-minimum interval — the "ideal" period (s). */
  idealPeriod: number | null;
  medianPeriod: number | null;
  /** How many raw KE minima are skipped per plotted point (N = 1 means every minimum). */
  skipCount: number;
  points: ClosurePoint[];
}

function smooth(arr: Float64Array, radius: number): Float64Array {
  if (radius < 1) return arr.slice();
  const n = arr.length;
  const out = new Float64Array(n);
  let sum = 0;
  const w = radius * 2 + 1;
  for (let i = 0; i < n; i++) {
    sum += arr[i];
    if (i >= w) sum -= arr[i - w];
    const lo = Math.max(0, i - radius);
    const hi = Math.min(n - 1, i + radius);
    out[i] = sum / (hi - lo + 1);
  }
  return out;
}

function median(vals: number[]): number {
  if (vals.length === 0) return 0;
  const s = [...vals].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : 0.5 * (s[m - 1] + s[m]);
}

export function computeClosure(
  ke: Float64Array,
  channels: Float64Array[],
  len: number,
  dt: number,
): ClosureResult {
  const empty: ClosureResult = { idealPeriod: null, medianPeriod: null, skipCount: 1, points: [] };
  if (len < 64 || dt <= 0 || channels.length === 0) return empty;

  // decimate everything to ~128 Hz — enough to resolve a turning point sharply,
  // few enough samples that minima are clean and cheap to find.
  const rawFs = 1 / dt;
  const dec = Math.max(1, Math.round(rawFs / 128));
  const dLen = Math.floor(len / dec);
  if (dLen < 16) return empty;
  const tStep = dec * dt;

  const keD = new Float64Array(dLen);
  const chanD: Float64Array[] = channels.map(() => new Float64Array(dLen));
  for (let i = 0; i < dLen; i++) {
    const base = i * dec;
    let s = 0;
    for (let j = 0; j < dec; j++) s += ke[base + j];
    keD[i] = s / dec;
    for (let c = 0; c < channels.length; c++) {
      let sc = 0;
      for (let j = 0; j < dec; j++) sc += channels[c][base + j];
      chanD[c][i] = sc / dec;
    }
  }

  // light smoothing to kill sample noise without broadening the minima
  const sm = smooth(keD, Math.max(1, Math.round(dLen * 0.004)));
  let keMin = Infinity, keMax = -Infinity;
  for (let i = 0; i < dLen; i++) {
    if (sm[i] < keMin) keMin = sm[i];
    if (sm[i] > keMax) keMax = sm[i];
  }
  const range = keMax - keMin || 1;
  const thr = keMin + 0.3 * range; // only deep minima (the real turning points) dip below this

  // turning points = local minima of excursions below the threshold
  const minIdx: number[] = [];
  let i = 0;
  while (i < dLen) {
    if (sm[i] < thr) {
      let j = i, bestIdx = i;
      let bestVal = sm[i];
      while (j < dLen && sm[j] < thr) {
        if (sm[j] < bestVal) { bestVal = sm[j]; bestIdx = j; }
        j++;
      }
      minIdx.push(bestIdx);
      i = j;
    } else {
      i++;
    }
  }
  if (minIdx.length < 2) return empty;

  // parabolic refinement of each minimum's index
  const refined = minIdx.map((m) => {
    if (m <= 0 || m >= dLen - 1) return m;
    const y0 = sm[m - 1], y1 = sm[m], y2 = sm[m + 1];
    const d = y0 - 2 * y1 + y2;
    return m + (d !== 0 ? 0.5 * (y0 - y2) / d : 0);
  });

  // per-channel normalisation so the phase-space distance is unit-balanced
  const nchan = channels.length;
  const std: number[] = [];
  for (let c = 0; c < nchan; c++) {
    let mean = 0;
    for (let k = 0; k < dLen; k++) mean += chanD[c][k];
    mean /= dLen;
    let v = 0;
    for (let k = 0; k < dLen; k++) { const dd = chanD[c][k] - mean; v += dd * dd; }
    std[c] = Math.sqrt(v / dLen) || 1;
  }
  const vecAt = (idx: number): Float64Array => {
    const ii = Math.max(0, Math.min(dLen - 1, Math.round(idx)));
    const v = new Float64Array(nchan);
    for (let c = 0; c < nchan; c++) v[c] = chanD[c][ii] / std[c];
    return v;
  };
  const dist = (a: Float64Array, b: Float64Array): number => {
    let s = 0;
    for (let c = 0; c < nchan; c++) { const dd = a[c] - b[c]; s += dd * dd; }
    return Math.sqrt(s);
  };

  // A multi-body pendulum may have several KE minima per true orbit loop
  // (e.g. upper bob + lower bob each dip independently).  Instead of counting
  // skips, compare each minimum's phase-space state to the section anchor:
  // only local minima of that distance are genuine returns to the same crossing.
  const vectors: Float64Array[] = refined.map((r) => vecAt(r));
  const dists: number[] = vectors.map((v) => dist(v, vectors[0]));

  // A true return is a local minimum of the distance-to-anchor curve,
  // separated from the last accepted return by at least a refractory gap
  // (avoid catching two dips from the same loop).
  const avgPeriod = ((refined[refined.length - 1] - refined[0]) * tStep) / refined.length;
  const refracIdx = Math.max(1, Math.floor(avgPeriod * 0.3 / tStep)); // ~30% of avg spacing

  const picked: number[] = [0]; // indices into refined[]
  for (let k = 1; k < refined.length - 1; k++) {
    // must be a true local minimum of the distance curve
    if (dists[k] >= dists[k - 1] || dists[k] >= dists[k + 1]) continue;
    // must be separated from the last pick by at least the refractory gap
    if (refined[k] - refined[picked[picked.length - 1]] < refracIdx) continue;
    picked.push(k);
  }

  if (picked.length < 2) return empty;

  // Build closure points using only the confirmed Poincaré returns.
  const v0 = vectors[0];
  const points: ClosurePoint[] = [];
  const periods: number[] = [];
  let prevIdx = refined[picked[0]];
  for (let i = 0; i < picked.length; i++) {
    const k = picked[i];
    const t = refined[k] * tStep;
    const period = i > 0 ? (refined[k] - prevIdx) * tStep : null;
    if (period != null) periods.push(period);
    points.push({ time: t, period, stray: dist(vectors[k], v0) });
    prevIdx = refined[k];
  }

  return {
    idealPeriod: periods.length ? periods[0] : null,
    medianPeriod: median(periods),
    skipCount: picked.length,
    points,
  };
}

export interface ClosurePanel {
  resize(w: number, h: number): void;
  refresh(): void;
  clear(): void;
}

export function createClosurePanel(opts: {
  canvas: HTMLCanvasElement;
  info: HTMLElement;
  analyzeBtn: HTMLButtonElement;
  liveToggle: HTMLInputElement;
  getData: () => { ke: Float64Array; channels: Float64Array[]; len: number; dt: number } | null;
}): ClosurePanel {
  const { canvas, info, analyzeBtn, liveToggle, getData } = opts;
  const ctx = canvas.getContext('2d')!;
  let width = canvas.width;
  let height = canvas.height;
  let last: ClosureResult | null = null;

  function run(): void {
    const data = getData();
    if (!data) {
      info.textContent = 'Not enough samples yet — let the simulation run.';
      last = null;
      draw();
      return;
    }
    last = computeClosure(data.ke, data.channels, data.len, data.dt);
    draw();
  }

  function draw(): void {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(244,240,228,1)';
    ctx.fillRect(0, 0, width, height);

    const padL = 44, padR = 46, padT = 16, padB = 22;
    const plotW = width - padL - padR;
    const plotH = height - padT - padB;
    if (plotW <= 0 || plotH <= 0) return;

    if (!last || last.points.length < 2) {
      ctx.fillStyle = 'rgba(133,96,49,0.7)';
      ctx.font = 'italic 400 13px "Cormorant Garamond", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Run the simulation, then press “Analyze Closure”', width / 2, height / 2);
      return;
    }

    const pts = last.points;
    const tMin = pts[0].time;
    const tMax = pts[pts.length - 1].time;
    const tRange = Math.max(1e-6, tMax - tMin);
    let strayMax = 1e-6;
    for (const p of pts) if (p.stray > strayMax) strayMax = p.stray;
    strayMax *= 1.15;
    let pMin = Infinity, pMax = -Infinity;
    for (const p of pts) if (p.period != null) { if (p.period < pMin) pMin = p.period; if (p.period > pMax) pMax = p.period; }
    if (!isFinite(pMin) || !isFinite(pMax)) { pMin = 0; pMax = 1; }
    const pPad = (pMax - pMin) * 0.15 || 0.5;
    pMin -= pPad; pMax += pPad;

    const xOf = (t: number) => padL + ((t - tMin) / tRange) * plotW;
    const yStray = (s: number) => padT + (1 - s / strayMax) * plotH;
    const yPeriod = (p: number) => padT + (1 - (p - pMin) / (pMax - pMin)) * plotH;

    // grid
    ctx.strokeStyle = 'rgba(133,96,49,0.16)';
    ctx.lineWidth = 0.6;
    for (let g = 0; g <= 4; g++) {
      const y = padT + (plotH * g) / 4;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(width - padR, y); ctx.stroke();
    }

    // ideal-period reference (right axis)
    if (last.idealPeriod != null) {
      ctx.strokeStyle = 'rgba(169,130,74,0.5)';
      ctx.setLineDash([4, 4]);
      const y = yPeriod(last.idealPeriod);
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(width - padR, y); ctx.stroke();
      ctx.setLineDash([]);
    }

    // drift curve (left axis, blue) — "how the orbit strays"
    ctx.strokeStyle = 'rgba(44,70,131,0.95)';
    ctx.fillStyle = 'rgba(44,70,131,0.95)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    pts.forEach((p, k) => {
      const x = xOf(p.time), y = yStray(p.stray);
      if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
    for (const p of pts) {
      ctx.beginPath(); ctx.arc(xOf(p.time), yStray(p.stray), 2.6, 0, Math.PI * 2); ctx.fill();
    }

    // per-loop period curve (right axis, gold)
    ctx.strokeStyle = 'rgba(133,96,49,0.9)';
    ctx.fillStyle = 'rgba(133,96,49,0.9)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    let started = false;
    for (const p of pts) {
      if (p.period == null) continue;
      const x = xOf(p.time), y = yPeriod(p.period);
      if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
    }
    ctx.stroke();
    for (const p of pts) {
      if (p.period == null) continue;
      ctx.beginPath(); ctx.arc(xOf(p.time), yPeriod(p.period), 2.2, 0, Math.PI * 2); ctx.fill();
    }

    // axis labels
    ctx.fillStyle = 'rgba(44,70,131,0.85)';
    ctx.font = '500 10px "Cormorant Garamond", Georgia, serif';
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    for (let g = 0; g <= 4; g++) {
      const s = (strayMax * (4 - g)) / 4;
      ctx.fillText(s.toFixed(2), padL - 5, padT + (plotH * g) / 4);
    }
    ctx.fillStyle = 'rgba(133,96,49,0.85)';
    ctx.textAlign = 'left';
    for (let g = 0; g <= 4; g++) {
      const p = pMin + ((pMax - pMin) * (4 - g)) / 4;
      ctx.fillText(p.toFixed(2), width - padR + 5, padT + (plotH * g) / 4);
    }
    ctx.fillStyle = 'rgba(133,96,49,0.8)';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    const xticks = 5;
    for (let g = 0; g <= xticks; g++) {
      const t = tMin + (tRange * g) / xticks;
      ctx.fillText(`${t.toFixed(1)}s`, padL + (plotW * g) / xticks, height - padB + 4);
    }
    // axis titles
    ctx.fillStyle = 'rgba(44,70,131,0.8)';
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText('drift \u2191', padL, 2);
    ctx.fillStyle = 'rgba(133,96,49,0.8)';
    ctx.textAlign = 'right';
    ctx.fillText('\u2191 loop period (s)  ·  - - ideal', width - padR, 2);

    // readout
    const n = pts.length;
    const finalStray = pts[n - 1].stray;
    const ip = last.idealPeriod, mp = last.medianPeriod;
    info.textContent =
      `ideal period T* = ${ip != null ? ip.toFixed(3) + ' s' : '—'}`
      + `   ·   median ${mp != null ? mp.toFixed(3) + ' s' : '—'}`
      + `   ·   ${n} returns`
      + `   ·   final drift ${finalStray.toFixed(3)}`;
  }

  analyzeBtn.addEventListener('click', run);
  function resize(w: number, h: number): void { width = w; height = h; canvas.width = w; canvas.height = h; draw(); }
  function refresh(): void { if (liveToggle.checked) run(); }
  function clear(): void { last = null; info.textContent = ''; draw(); }

  return { resize, refresh, clear };
}
